import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Search, Loader2, Package, AlertTriangle, Check, ChevronRight, X, ArrowRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { formatDate } from '@/utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BookToWOModal = ({ open, onOpenChange, inventoryItem, onSuccess, user }) => {
  const [step, setStep] = useState('select'); // 'select' | 'confirm'
  const [eligibleOrders, setEligibleOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [barsToBook, setBarsToBook] = useState('');

  const fetchEligibleOrders = useCallback(async () => {
    if (!inventoryItem?.inventory_id) return;
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/material-allocations/eligible-orders`, {
        params: { inventory_id: inventoryItem.inventory_id }
      });
      setEligibleOrders(res.data);
    } catch (err) {
      toast.error('Failed to load eligible works orders');
    } finally {
      setLoading(false);
    }
  }, [inventoryItem?.inventory_id]);

  useEffect(() => {
    if (open && inventoryItem) {
      setStep('select');
      setSelectedOrder(null);
      setBarsToBook('');
      setSearchQuery('');
      fetchEligibleOrders();
    }
  }, [open, inventoryItem, fetchEligibleOrders]);

  const filteredOrders = eligibleOrders.filter(o => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      o.order_number?.toLowerCase().includes(q) ||
      o.customer?.toLowerCase().includes(q) ||
      o.part_number?.toLowerCase().includes(q) ||
      o.description?.toLowerCase().includes(q)
    );
  });

  const handleConfirmStep = () => {
    const qty = parseInt(barsToBook);
    if (!selectedOrder) {
      toast.error('Please select a works order');
      return;
    }
    if (!qty || qty <= 0) {
      toast.error('Please enter a valid positive number of bars');
      return;
    }
    if (qty > (inventoryItem?.quantity_available || 0)) {
      toast.error(`Cannot book more than ${inventoryItem?.quantity_available} available bars`);
      return;
    }
    setStep('confirm');
  };

  const handleBook = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/material-allocations/book`, {
        inventory_id: inventoryItem.inventory_id,
        order_number: selectedOrder.order_number,
        bars_booked: parseInt(barsToBook),
        booked_by: user?.email || 'System'
      });
      toast.success(res.data.message);
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Booking failed';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  if (!inventoryItem) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-cyan-500/30 max-w-2xl max-h-[85vh] flex flex-col" data-testid="book-to-wo-modal">
        <DialogHeader>
          <DialogTitle className="text-white uppercase tracking-wide flex items-center gap-2">
            <Package className="w-5 h-5 text-cyan-400" />
            {step === 'select' ? 'Book Raw Material to Works Order' : 'Confirm Booking'}
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">
            {step === 'select' 
              ? 'Select a works order in the Cutting stage to allocate bars' 
              : 'Review and confirm the material allocation'}
          </DialogDescription>
        </DialogHeader>

        {/* Material Info Banner */}
        <div className="bg-slate-800/80 border border-slate-700 rounded-lg p-3 flex items-center justify-between" data-testid="material-info-banner">
          <div className="flex items-center gap-4">
            <div>
              <p className="text-[10px] text-slate-500 uppercase">Material</p>
              <p className="text-white font-bold">{inventoryItem.material_grade}</p>
            </div>
            <div>
              <p className="text-[10px] text-slate-500 uppercase">Cast/Heat No</p>
              <p className="text-purple-400 font-mono text-sm">{inventoryItem.cast_number || 'N/A'}</p>
            </div>
            <div>
              <p className="text-[10px] text-slate-500 uppercase">Diameter</p>
              <p className="text-cyan-400 font-mono text-sm">{inventoryItem.diameter} mm</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-[10px] text-slate-500 uppercase">Available</p>
            <p className="text-2xl font-bold text-green-400 font-mono" data-testid="available-qty">{inventoryItem.quantity_available}</p>
            <p className="text-[10px] text-slate-500">{inventoryItem.unit || 'bars'}</p>
          </div>
        </div>

        {step === 'select' && (
          <div className="flex flex-col gap-4 overflow-hidden flex-1 min-h-0">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by WO number, customer, part..."
                className="bg-slate-800 border-slate-700 pl-10"
                data-testid="wo-search-input"
              />
            </div>

            {/* Eligible Orders List */}
            <div className="flex-1 overflow-y-auto border border-slate-700 rounded-lg min-h-[200px] max-h-[300px]" data-testid="eligible-orders-list">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-cyan-400" />
                </div>
              ) : filteredOrders.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                  <AlertTriangle className="w-8 h-8 mb-2 text-amber-500/50" />
                  <p className="text-sm">No eligible works orders found</p>
                  <p className="text-xs mt-1">Only orders currently in the Cutting stage are shown</p>
                </div>
              ) : (
                filteredOrders.map((order) => (
                  <div
                    key={order.order_number}
                    onClick={() => setSelectedOrder(order)}
                    className={`p-3 border-b border-slate-800 cursor-pointer transition-colors ${
                      selectedOrder?.order_number === order.order_number
                        ? 'bg-cyan-500/10 border-l-2 border-l-cyan-400'
                        : 'hover:bg-slate-800/50'
                    }`}
                    data-testid={`wo-option-${order.order_number}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div>
                          <p className="text-white font-mono font-bold text-sm">{order.order_number}</p>
                          <p className="text-slate-400 text-xs">{order.customer}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">{order.description || order.part_number}</p>
                          {order.required_material && (
                            <p className="text-xs text-slate-500">
                              Material: <span className={order.material_compatible ? 'text-green-400' : 'text-red-400'}>
                                {order.required_material}
                              </span>
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        {order.bars_required != null && (
                          <p className="text-xs font-bold">
                            <span className="text-slate-500">Bars Req: </span>
                            <span className="text-amber-400 font-mono">{order.bars_required}</span>
                            {order.total_bars_already_booked > 0 && (
                              <span className="text-slate-500"> (still need {Math.max(0, order.bars_required - order.total_bars_already_booked)})</span>
                            )}
                          </p>
                        )}
                        <p className="text-xs text-slate-500">Due: {formatDate(order.due_date)}</p>
                        <p className="text-xs text-slate-500">Qty: {order.quantity} pcs</p>
                        {order.total_bars_already_booked > 0 && (
                          <p className="text-xs text-cyan-400">{order.total_bars_already_booked} {order.total_bars_already_booked === 1 ? 'bar' : 'bars'} already booked</p>
                        )}
                        {!order.material_compatible && (
                          <span className="text-[10px] bg-red-950 text-red-400 px-1.5 py-0.5 rounded">Material Mismatch</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Quantity Input */}
            {selectedOrder && (
              <div className="flex items-end gap-4">
                <div className="flex-1">
                  <Label className="text-slate-400 uppercase text-xs">Number of Bars to Book</Label>
                  <Input
                    type="number"
                    min="1"
                    max={inventoryItem.quantity_available}
                    step="1"
                    value={barsToBook}
                    onChange={(e) => setBarsToBook(e.target.value)}
                    className="bg-slate-800 border-slate-700 text-lg font-mono text-center"
                    placeholder="Enter quantity..."
                    data-testid="bars-quantity-input"
                  />
                </div>
                <Button
                  onClick={handleConfirmStep}
                  disabled={!barsToBook || parseInt(barsToBook) <= 0}
                  className="bg-cyan-600 hover:bg-cyan-500 h-10"
                  data-testid="proceed-to-confirm-btn"
                >
                  Review <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}
          </div>
        )}

        {step === 'confirm' && selectedOrder && (
          <div className="space-y-4">
            {/* Confirmation Summary */}
            <div className="bg-amber-950/30 border border-amber-500/20 rounded-lg p-4 space-y-3" data-testid="booking-confirmation-summary">
              <h4 className="text-amber-400 font-bold uppercase text-xs tracking-wider">Booking Summary</h4>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500 text-xs">Works Order</p>
                  <p className="text-white font-mono font-bold">{selectedOrder.order_number}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Customer</p>
                  <p className="text-white">{selectedOrder.customer}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Part / Description</p>
                  <p className="text-white">{selectedOrder.description || selectedOrder.part_number}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Due Date</p>
                  <p className="text-white">{formatDate(selectedOrder.due_date)}</p>
                </div>
                {selectedOrder.bars_required != null && (
                  <div>
                    <p className="text-slate-500 text-xs">Bars Required by WO</p>
                    <p className="text-amber-400 font-mono font-bold">{selectedOrder.bars_required}</p>
                  </div>
                )}
              </div>

              <div className="border-t border-amber-500/20 pt-3 mt-3">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-[10px] text-slate-500 uppercase">Cast Number</p>
                    <p className="text-purple-400 font-mono font-bold">{inventoryItem.cast_number || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-500 uppercase">Bars to Book</p>
                    <p className="text-cyan-400 font-mono font-bold text-xl">{barsToBook}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-500 uppercase">Remaining After</p>
                    <p className={`font-mono font-bold text-xl ${(inventoryItem.quantity_available - parseInt(barsToBook)) <= 0 ? 'text-red-400' : 'text-green-400'}`}>
                      {inventoryItem.quantity_available - parseInt(barsToBook)}
                    </p>
                  </div>
                </div>
              </div>

              {!selectedOrder.material_compatible && (
                <div className="flex items-center gap-2 bg-red-950/50 border border-red-500/30 rounded p-2">
                  <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
                  <p className="text-red-300 text-xs">
                    Warning: Material grade does not match the works order requirement. 
                    Order requires {selectedOrder.required_material}, inventory has {inventoryItem.material_grade}.
                  </p>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setStep('select')}
                className="border-slate-600"
                data-testid="back-to-select-btn"
              >
                <ChevronRight className="w-4 h-4 mr-1 rotate-180" /> Back
              </Button>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  className="border-slate-600"
                  data-testid="cancel-booking-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleBook}
                  disabled={submitting}
                  className="bg-green-600 hover:bg-green-500"
                  data-testid="confirm-booking-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                  {submitting ? 'Booking...' : 'Confirm Booking'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BookToWOModal;
